import { environment } from "../env";
import { getToken } from "../auth/token";
import { linkSignals } from "./abort";

// SAME CEILING AS THE ANGULAR `TokenInterceptor` (`timeout(80000)`)
const REQUEST_TIMEOUT_MS = 80_000;

export class ApiError extends Error {
  readonly status: number;
  // WHAT THE SERVER SAID IN ITS ERROR BODY, WHEN IT SAID ANYTHING (SEE `errorMessage`)
  readonly serverMessage: string | undefined;
  constructor(status: number, message: string, serverMessage?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

// PORT OF `UiService.GetErrorMessage`, WHICH MANY ANGULAR TOASTS SHOW VERBATIM: THE ERROR
// BODY'S `message.Message`, THEN A PLAIN-STRING BODY, THEN `message`, THEN A FIXED FALLBACK
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError && e.serverMessage) return e.serverMessage;
  return "An error occurred. Please try again.";
}

function readServerMessage(text: string): string | undefined {
  if (!text) return undefined;
  try {
    const body: unknown = JSON.parse(text);
    if (typeof body === "string") return body || undefined;
    if (body && typeof body === "object" && "message" in body) {
      const m = (body as { message: unknown }).message;
      if (m && typeof m === "object" && "Message" in m && typeof (m as { Message: unknown }).Message === "string") {
        return (m as { Message: string }).Message;
      }
      if (typeof m === "string" && m) return m;
    }
    return undefined;
  } catch {
    // A NON-JSON BODY IS THE SERVER'S PLAIN TEXT
    return text;
  }
}

// EVERY CORE API V2 RESPONSE WRAPS ITS PAYLOAD AS `{ data }`
export interface Envelope<T> {
  data: T;
}

// SERVER-SIDE PAGE, AS RETURNED BY THE `/ht/paged-*` AND `/player/paged-*` ENDPOINTS
export interface PagedData<T> {
  rows: T[];
  page: number;
  totalRows: number;
  totalPages: number;
  has_next: boolean;
}

// SET BY `main.tsx` ONCE THE ROUTER EXISTS. KEPT AS A HOOK RATHER THAN AN IMPORT
// SO THIS MODULE DOES NOT DEPEND ON THE ROUTER.
let onUnauthenticated: () => void = () => {};
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  // SENT AS THE RAW REQUEST BODY WITH THIS CONTENT TYPE, NOT JSON-ENCODED (DICTATION AUDIO)
  raw?: { body: Blob; contentType: string };
  signal?: AbortSignal;
  // `/api/authenticate` IS THE ONE CALL MADE WITHOUT A TOKEN
  anonymous?: boolean;
}

export function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return send(path, options, "application/json", async (resp) => {
    // A 200 WITH NO BODY (SOME WRITE ENDPOINTS) READS AS AN EMPTY ENVELOPE, NOT A PARSE ERROR
    const text = await resp.text();
    return (text ? JSON.parse(text) : { data: null }) as T;
  });
}

// THE SAME REQUEST, READ AS A FILE (CSV EXPORTS: ANGULAR'S `responseType: "blob"`)
export function apiBlobRequest(path: string, options: RequestOptions = {}): Promise<Blob> {
  return send(path, options, "*/*", (resp) => resp.blob());
}

// `read` RUNS INSIDE THE REQUEST'S LIFETIME, SO THE CEILING AND CANCELLATION ALSO COVER READING THE BODY
async function send<R>(path: string, options: RequestOptions, accept: string, read: (resp: Response) => Promise<R>): Promise<R> {
  const { method = "GET", body, raw, signal, anonymous = false } = options;

  const headers: Record<string, string> = {
    Accept: accept,
    "Content-Type": raw ? raw.contentType : "application/json",
  };

  if (!anonymous) {
    const token = getToken();
    if (!token) {
      onUnauthenticated();
      throw new ApiError(401, "Not signed in");
    }
    headers["Authorization"] = `Bearer ${token}`;
  }

  // TANSTACK QUERY'S SIGNAL CANCELS A SUPERSEDED REQUEST (PAGE/SORT/SITE CHANGE,
  // UNMOUNT); THE CEILING ENFORCES THE TIMEOUT. EITHER ONE ABORTS (SEE `linkSignals`: NOT
  // `AbortSignal.any`, WHICH OLDER NATIVE-SHELL WEBVIEWS LACK).
  const linked = linkSignals([signal], REQUEST_TIMEOUT_MS);
  try {
    const resp = await fetch(environment.api + path, {
      method,
      headers,
      body: raw ? raw.body : body === undefined ? undefined : JSON.stringify(body),
      signal: linked.signal,
    });

    if (resp.status === 401 && !anonymous) {
      onUnauthenticated();
    }

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new ApiError(resp.status, `${method} ${path} failed with ${resp.status}`, readServerMessage(text));
    }
    return await read(resp);
  } finally {
    linked.dispose();
  }
}

// BUILDS THE `page`/`page_size`/`order_by`/`order_dir` QUERY STRING THE PAGED ENDPOINTS TAKE
export interface PageRequest {
  page: number; // 1-BASED, AS THE API EXPECTS
  pageSize: number;
  orderBy: string;
  orderDir: "asc" | "desc";
}

export function pageQuery(p: PageRequest, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams({
    ...extra,
    page: String(p.page),
    page_size: String(p.pageSize),
    order_by: p.orderBy,
    order_dir: p.orderDir,
  });
  return params.toString();
}

// A PAGED RESPONSE WITHOUT A ROWS ARRAY IS SHAPE DRIFT. THROWING TURNS IT INTO THE
// QUERY'S ERROR STATE (WHICH EVERY PAGE ALREADY RENDERS) INSTEAD OF A CRASH IN
// WHATEVER COMPONENT FIRST READS `rows.length`.
export function pagedOrThrow<T>(data: PagedData<T> | null | undefined, what: string): PagedData<T> {
  if (!data || !Array.isArray(data.rows)) {
    throw new ApiError(200, `${what} returned no rows array`);
  }
  return data;
}
